import React, { useState, useEffect } from 'react';
import { 
  Radar, 
  Lightbulb,
  AlertTriangle, 
  Zap,
  Globe,
  Clock,
  RefreshCw,
  Calendar,
  Mail,
  ArrowRight,
  TrendingUp,
  CheckCircle,
  Send,
  X,
  Info,
  ChevronRight,
  Target,
  BrainCircuit,
  Shield,
  Users,
  PieChart,
  Heart,
  ShoppingCart,
  Cpu,
  Search,
  FileText,
  Phone
} from 'lucide-react';

// Import components from main site
import HeaderNav from './HeaderNav';
import FooterLan from './FooterLan';

// Color palette aligned with main site
const COLORS = {
  primary: '#ce2223',
  primaryDark: '#b01a1b',
  primaryLight: '#f8e7e7',
  primaryLighter: '#fef2f2',
  secondary: '#fdb52a',
  secondaryDark: '#e09c18',
  secondaryLight: '#fff8eb',
  black: '#111827',
  darkGray: '#374151',
  mediumGray: '#6b7280',
  lightGray: '#e5e7eb',
  offWhite: '#f9fafb',
  white: '#ffffff',
  success: '#10b981',
  successLight: '#ecfdf5'
};

// Signal categories with better icons and colors
const SIGNAL_CATEGORIES = {
  technology: {
    label: 'Technology Trends',
    icon: Zap,
    bgClass: 'bg-blue-50',
    textClass: 'text-blue-700',
    borderClass: 'border-blue-200',
    iconBg: 'bg-blue-100'
  },
  social: {
    label: 'Social & Behavioral Changes',
    icon: Users,
    bgClass: 'bg-green-50',
    textClass: 'text-green-700',
    borderClass: 'border-green-200',
    iconBg: 'bg-green-100'
  },
  economic: {
    label: 'Economic Shifts',
    icon: TrendingUp,
    bgClass: 'bg-amber-50',
    textClass: 'text-amber-700',
    borderClass: 'border-amber-200',
    iconBg: 'bg-amber-100'
  },
  regulatory: {
    label: 'Regulatory Changes',
    icon: Shield,
    bgClass: 'bg-purple-50',
    textClass: 'text-purple-700',
    borderClass: 'border-purple-200',
    iconBg: 'bg-purple-100'
  }
};

// Example business challenges
const EXAMPLE_CHALLENGES = [
  {
    text: "How can new technology help improve our operations and stay competitive?",
    icon: <BrainCircuit className="h-4 w-4" />,
    color: "bg-blue-50 text-blue-700 border-blue-200"
  },
  {
    text: "How should we adapt to changing customer expectations and preferences?",
    icon: <Users className="h-4 w-4" />,
    color: "bg-green-50 text-green-700 border-green-200"
  },
  {
    text: "What new technologies should we consider investing in for future growth?",
    icon: <Cpu className="h-4 w-4" />,
    color: "bg-purple-50 text-purple-700 border-purple-200"
  },
  {
    text: "How can we make our business more environmentally sustainable?",
    icon: <Globe className="h-4 w-4" />,
    color: "bg-emerald-50 text-emerald-700 border-emerald-200"
  },
  {
    text: "How do we compete with new companies entering our industry?",
    icon: <Target className="h-4 w-4" />,
    color: "bg-red-50 text-red-700 border-red-200"
  },
  {
    text: "What should we do to prepare for economic uncertainty?",
    icon: <TrendingUp className="h-4 w-4" />,
    color: "bg-amber-50 text-amber-700 border-amber-200"
  }
];

// Improved Button component
const Button = ({ 
  children, 
  onClick, 
  isLoading = false, 
  fullWidth = false, 
  variant = 'primary', 
  icon, 
  className = '', 
  disabled = false,
  size = 'md',
  type = 'button'
}) => {
  const baseStyle = "font-medium transition-all duration-300 flex items-center justify-center shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500";
  
  const sizes = {
    sm: "py-2 px-4 text-sm rounded-md",
    md: "py-3 px-6 text-base rounded-lg",
    lg: "py-4 px-8 text-lg rounded-xl"
  };
  
  const variants = {
    primary: `bg-red-600 hover:bg-red-700 text-white ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`,
    outline: `bg-white border-2 border-red-600 text-red-600 hover:bg-red-50 ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'}`,
    secondary: `bg-gray-100 hover:bg-gray-200 text-gray-700 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={isLoading || disabled}
      className={`${baseStyle} ${sizes[size]} ${variants[variant]} ${fullWidth ? 'w-full' : ''} ${className}`}
    >
      {isLoading ? (
        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
      ) : (
        icon && <span className="mr-2">{icon}</span>
      )}
      {children}
    </button>
  );
};

// Toast notification with better mobile support
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 5000);
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const styles = {
    success: 'bg-green-50 text-green-800 border-green-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    info: 'bg-blue-50 text-blue-800 border-blue-200'
  };
  
  return (
    <div className={`fixed top-20 left-4 right-4 sm:right-4 sm:left-auto sm:max-w-sm z-50 px-4 py-3 rounded-lg shadow-lg border flex items-center gap-3 ${styles[type]}`}>
      <CheckCircle className="h-5 w-5 flex-shrink-0" />
      <p className="font-medium flex-1">{message}</p>
      <button 
        onClick={onClose} 
        className="flex-shrink-0 p-1 hover:bg-black/10 rounded"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

// Signal card with mobile improvements
const SignalCard = ({ signal, index }) => {
  const category = SIGNAL_CATEGORIES[signal.category] || SIGNAL_CATEGORIES.technology;
  const CategoryIcon = category.icon;
  
  const getTimeframeLabel = (years) => {
    if (years <= 2) return "Immediate (1-2 years)";
    if (years <= 5) return "Near-term (3-5 years)";
    return "Long-term (5+ years)";
  };
  
  const getTimeframeColor = (years) => {
    if (years <= 2) return "bg-red-100 text-red-700";
    if (years <= 5) return "bg-amber-100 text-amber-700";
    return "bg-green-100 text-green-700";
  };
  
  return (
    <article className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
      <div className="p-4 sm:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${category.iconBg} flex items-center justify-center flex-shrink-0`}>
              <CategoryIcon className={`h-5 w-5 ${category.textClass}`} />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-bold text-gray-900 text-lg leading-tight">{signal.title}</h3>
              <span className={`text-sm ${category.textClass} font-medium`}>{category.label}</span>
            </div>
          </div>
          <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 font-medium ${getTimeframeColor(signal.timeframe)}`}>
            {getTimeframeLabel(signal.timeframe)}
          </span>
        </div>
        
        {/* Description */}
        <p className="text-gray-700 mb-4 leading-relaxed">{signal.description}</p>
        
        {/* Action */}
        {signal.action && (
          <div className={`${category.bgClass} border ${category.borderClass} rounded-lg p-4`}>
            <div className="flex items-start gap-3">
              <ArrowRight className={`h-4 w-4 ${category.textClass} mt-1 flex-shrink-0`} />
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-1">Recommended Action:</h4>
                <p className="text-sm text-gray-700 leading-relaxed">{signal.action}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

// Loading animation with better mobile support
const LoadingAnimation = () => {
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => Math.min(prev + 2, 95));
    }, 100);
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 sm:p-8 max-w-md mx-auto text-center">
      <div className="w-16 h-16 mx-auto mb-6">
        <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
          <circle
            className="text-gray-200"
            strokeWidth="8"
            stroke="currentColor"
            fill="transparent"
            r="40"
            cx="50"
            cy="50"
          />
          <circle
            className="text-red-600 transition-all duration-300"
            strokeWidth="8"
            strokeDasharray={251}
            strokeDashoffset={251 - (progress / 100) * 251}
            strokeLinecap="round"
            stroke="currentColor"
            fill="transparent"
            r="40"
            cx="50"
            cy="50"
          />
        </svg>
      </div>
      
      <h3 className="text-xl font-bold text-gray-900 mb-2">Analyzing Trends</h3>
      <div className="flex items-center justify-center gap-2 text-red-600 mb-4">
        <Search className="h-5 w-5" />
        <span className="font-medium">Scanning for relevant signals...</span>
      </div>
      <p className="text-gray-600 text-sm leading-relaxed">
        This takes about 30 seconds while we identify the most relevant trends and opportunities for your business challenge.
      </p>
    </div>
  );
};

// Email capture with mobile improvements
const EmailCapture = ({ onSubmit, isSubmitting }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  
  const handleSubmit = () => {
    if (!email || !email.includes('@')) return;
    onSubmit({ email, name });
  };
  
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-3">
          <FileText className="h-6 w-6 text-red-600" />
        </div>
        <h3 className="text-lg font-bold text-gray-900 mb-2">Get Detailed Report</h3>
        <p className="text-gray-600 text-sm">Receive a comprehensive PDF with action steps, timelines, and implementation guidance</p>
      </div>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="name-input" className="block text-sm font-medium text-gray-700 mb-2">
            Name
          </label>
          <input
            id="name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full p-3 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
          />
        </div>
        
        <div>
          <label htmlFor="email-input" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            id="email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            className="w-full p-3 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
          />
        </div>
        
        <Button
          onClick={handleSubmit}
          disabled={!email || !email.includes('@')}
          isLoading={isSubmitting}
          icon={<Send className="h-4 w-4" />}
          fullWidth
        >
          Send Me The Report
        </Button>
      </div>
      
      <p className="text-xs text-gray-500 text-center mt-4">
        We respect your privacy and never share your information
      </p>
    </div>
  );
};

// Success message
const SubmissionSuccess = ({ email }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 sm:p-8 text-center max-w-md mx-auto">
      <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-6">
        <CheckCircle className="h-8 w-8 text-green-600" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-3">Report Sent Successfully!</h3>
      <p className="text-gray-600 mb-4">
        Check your email at <span className="font-semibold text-gray-900">{email}</span> for your detailed trend analysis report.
      </p>
      <p className="text-gray-600 text-sm">
        Our team will follow up within 24 hours to discuss how these insights can be implemented in your business strategy.
      </p>
    </div>
  );
};

// Example challenge card with better mobile design
const ExampleCard = ({ challenge, onClick }) => {
  return (
    <button
      onClick={() => onClick(challenge.text)}
      className="p-4 bg-white border border-gray-200 rounded-lg text-left hover:border-red-300 hover:shadow-sm transition-all w-full group focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-8 h-8 rounded-lg border ${challenge.color} flex items-center justify-center`}>
          {challenge.icon}
        </div>
        <div className="text-xs text-gray-500 group-hover:text-red-600 transition-colors font-medium">
          Try this example →
        </div>
      </div>
      <p className="text-gray-800 font-medium text-sm leading-relaxed">
        {challenge.text}
      </p>
    </button>
  );
};

// Main component
const FutureSignalScanner = () => {
  const [businessChallenge, setBusinessChallenge] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [signals, setSignals] = useState([]);
  const [toast, setToast] = useState(null);
  const [isEmailSubmitting, setIsEmailSubmitting] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  
  const generateSignals = async () => {
    if (!businessChallenge.trim()) {
      setToast({ message: 'Please describe your business challenge first', type: 'error' });
      return;
    }
    
    setIsLoading(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    try {
      const response = await fetch('https://proxyapi-te6hpgqhgq-uc.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-3-5-sonnet-20241022',
          max_tokens: 3000,
          temperature: 0.7,
          messages: [{
            role: 'user',
            content: `Based on this business challenge: "${businessChallenge}", identify 5 clear future trends and market signals that could impact this organization.

For each signal, provide:
- Title: Clear, specific name describing the trend
- Category: technology, social, economic, or regulatory
- Description: 2 sentences explaining what this trend means and why it matters
- Timeframe: Years until significant impact (1-10)
- Action: One practical action the organization should take to prepare

Keep language simple and avoid technical jargon. Focus on practical, actionable insights.

Respond in this JSON format:
{
  "signals": [
    {
      "title": "Clear Trend Name",
      "category": "technology|social|economic|regulatory",
      "description": "Clear explanation of what this trend means and why it matters for businesses like this one.",
      "timeframe": 3,
      "action": "Simple, actionable step to take in response to this trend."
    }
  ]
}`
          }]
        })
      });

      if (!response.ok) throw new Error('Failed to generate trend analysis');
      
      const data = await response.json();
      const jsonMatch = data.content[0].text.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        const signalsData = JSON.parse(jsonMatch[0]);
        setTimeout(() => {
          setSignals(signalsData.signals || []);
          setIsLoading(false);
        }, 1500);
      } else {
        throw new Error('Could not parse analysis results');
      }
    } catch (error) {
      console.error('Error:', error);
      setIsLoading(false);
      setToast({ message: 'Unable to generate analysis. Please try again.', type: 'error' });
    }
  };
  
  const handleEmailSubmit = (data) => {
    setIsEmailSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsEmailSubmitting(false);
      setEmailSubmitted(true);
      setSubmittedEmail(data.email);
      setToast({ message: 'Report sent to your email successfully!', type: 'success' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 1500);
  };
  
  const handleReset = () => {
    setBusinessChallenge('');
    setSignals([]);
    setEmailSubmitted(false);
    setSubmittedEmail('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleSelectExample = (text) => {
    setBusinessChallenge(text);
    const textarea = document.getElementById('challenge-input');
    if (textarea) {
      setTimeout(() => {
        textarea.scrollIntoView({ behavior: 'smooth', block: 'center' });
        textarea.focus();
      }, 100);
    }
  };
  
  // SEO metadata (would be handled by helmet in real implementation)
  useEffect(() => {
    document.title = "Future Signal Scanner - Identify Business Trends & Opportunities | Lusidea";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Discover emerging trends and future opportunities that could impact your business. Get actionable insights and practical recommendations for strategic planning.');
    }
  }, []);
  
  return (
    <div className="min-h-screen bg-gray-50">
      <HeaderNav />
      
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      {/* Header Section */}
      <header className="bg-white pt-24 sm:pt-28 pb-12 sm:pb-16 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-16 h-16 mx-auto bg-red-100 rounded-2xl flex items-center justify-center mb-6">
            <Radar className="h-8 w-8 text-red-600" />
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 leading-tight">
            Future Signal Scanner
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Discover emerging trends and market opportunities that could impact your business in the coming years
          </p>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        
        {/* Input Section */}
        {!isLoading && signals.length === 0 && !emailSubmitted && (
          <div className="space-y-8">
            
            {/* Main Input */}
            <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                What business challenge are you facing?
              </h2>
              <div className="mb-6">
                <label htmlFor="challenge-input" className="block text-sm font-medium text-gray-700 mb-3">
                  Describe your challenge in detail
                </label>
                <textarea
                  id="challenge-input"
                  value={businessChallenge}
                  onChange={(e) => setBusinessChallenge(e.target.value)}
                  placeholder="Tell us about your business challenge... For example: 'We're a retail company struggling to keep up with digital transformation while maintaining our physical stores' or 'Our manufacturing business needs to understand how sustainability trends will affect our industry'"
                  className="w-full p-4 border border-gray-300 rounded-lg focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none min-h-32 text-gray-800 resize-y"
                  rows={6}
                />
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mt-3 gap-2">
                  <span className="text-sm text-gray-500">The more specific you are, the better insights we can provide</span>
                  <span className="text-sm text-gray-400">{businessChallenge.length} characters</span>
                </div>
              </div>
              <div className="text-center">
                <Button
                  onClick={generateSignals}
                  disabled={!businessChallenge.trim()}
                  icon={<Search className="h-5 w-5" />}
                  size="lg"
                >
                  Analyze Future Trends
                </Button>
              </div>
            </section>
            
            {/* Example Challenges */}
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-6 text-center">
                Not sure where to start? Try one of these examples:
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {EXAMPLE_CHALLENGES.map((challenge, idx) => (
                  <ExampleCard
                    key={idx}
                    challenge={challenge}
                    onClick={handleSelectExample}
                  />
                ))}
              </div>
            </section>
            
            {/* How it works */}
            <section className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <Info className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-3">How this tool works</h3>
                  <p className="text-blue-800 text-sm leading-relaxed">
                    Our analysis system examines your business challenge and identifies 5 key market trends and signals that could impact your organization. 
                    Each insight includes a timeline for when you can expect the impact and specific actions you can take to prepare and stay ahead.
                  </p>
                </div>
              </div>
            </section>
          </div>
        )}
        
        {/* Loading State */}
        {isLoading && <LoadingAnimation />}
        
        {/* Results Section */}
        {!isLoading && signals.length > 0 && !emailSubmitted && (
          <div className="space-y-8">
            {/* Results Header */}
            <section className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Your Future Trends Analysis</h2>
                  <p className="text-gray-600 text-sm">
                    Based on: <span className="font-medium">"{businessChallenge.slice(0, 100)}..."</span>
                  </p>
                </div>
              </div>
            </section>

            {/* Categories Legend */}
            <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4 text-center">Understanding Your Results</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {Object.entries(SIGNAL_CATEGORIES).map(([key, category]) => {
                  const CategoryIcon = category.icon;
                  return (
                    <div key={key} className="flex flex-col items-center text-center p-3 rounded-lg bg-gray-50">
                      <div className={`w-10 h-10 rounded-lg ${category.iconBg} flex items-center justify-center mb-2`}>
                        <CategoryIcon className={`h-5 w-5 ${category.textClass}`} />
                      </div>
                      <span className="text-sm text-gray-700 font-medium">{category.label}</span>
                    </div>
                  );
                })}
              </div>
            </section>
            
            {/* Signal Cards */}
            <section className="space-y-6">
              {signals.map((signal, idx) => (
                <SignalCard key={idx} signal={signal} index={idx} />
              ))}
            </section>

            {/* Email Capture */}
            <section className="max-w-2xl mx-auto">
              <EmailCapture onSubmit={handleEmailSubmit} isSubmitting={isEmailSubmitting} />
            </section>

            {/* Consultation CTA */}
            <section className="bg-gradient-to-r from-red-50 to-red-100 rounded-xl border border-red-200 p-6 sm:p-8 text-center">
              <div className="max-w-3xl mx-auto">
                <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Need Expert Guidance?</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">
                  Speak with our strategy experts about implementing these insights into your business planning and decision-making process. 
                  Get personalized recommendations and a clear action plan.
                </p>
                <a
                  href="https://calendly.com/lusidea/consultation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 py-3 px-6 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 shadow-sm"
                >
                  <Calendar className="h-5 w-5" />
                  Schedule Free Consultation
                </a>
              </div>
            </section>
            
            {/* Reset Action */}
            <div className="text-center pt-4">
              <Button
                onClick={handleReset}
                variant="outline"
                icon={<RefreshCw className="h-4 w-4" />}
              >
                Analyze Another Challenge
              </Button>
            </div>
          </div>
        )}
        
        {/* Success State */}
        {emailSubmitted && <SubmissionSuccess email={submittedEmail} />}
      </main>
      
      <FooterLan />
      
      <style jsx global>{`
        @media (max-width: 640px) {
          .line-clamp-3 {
            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
          }
        }
        
        /* Enhanced focus states for accessibility */
        input:focus, textarea:focus {
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }
        
        /* Smooth transitions */
        * {
          transition-property: background-color, border-color, color, fill, stroke, opacity, box-shadow, transform;
          transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
          transition-duration: 150ms;
        }
      `}</style>
    </div>
  );
};

export default FutureSignalScanner;